// /live: "Smartflo check" (admins). Today's calls read straight from Smartflo for
// the selected dealership's numbers, shown on the same dashboard as the panel's
// own data, plus a count of any call Smartflo has that the panel never recorded.
import { formatDid } from "@/lib/phone";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { runSmartfloCheck } from "@/integrations/firebase/admin-ops";
import { DashboardView } from "@/components/dashboard-view";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/_app/live")({
  head: () => ({ meta: [{ title: "Smartflo check — EESWEB" }] }),
  component: SmartfloCheckPage,
});

function SmartfloCheckPage() {
  const { role, effectiveClientId, clientName } = useAuth();
  const since = new Date(); since.setHours(0, 0, 0, 0);

  const { data, error, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["smartflo-check", effectiveClientId, since.toISOString()],
    queryFn: () => runSmartfloCheck({ clientId: effectiveClientId, since }),
    enabled: role === "admin",
    refetchInterval: 120_000, // Smartflo's API is slow; every 2 minutes is plenty for a cross-check
    retry: 1,
  });

  if (role && role !== "admin") return <Navigate to="/dashboard" replace />;

  const dateLabel = since.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  const scope = effectiveClientId ? clientName ?? "this dealership" : "all dealerships";
  const missing = data?.missing.length ?? 0;

  return (
    <DashboardView
      title="Smartflo check"
      subtitle={`${dateLabel} · ${scope} · straight from Smartflo's call records`}
      refreshNote={dataUpdatedAt ? `Checked ${new Date(dataUpdatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · every 2 min` : "Checking…"}
      todayCalls={error ? [] : data?.calls}
      linkCalls={false}
    >
      {error ? (
        <div className="flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <div className="flex-1">
            <p className="font-medium">Couldn't read Smartflo</p>
            <p className="text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn("size-4", isFetching && "animate-spin")} /> Retry
          </Button>
        </div>
      ) : data ? (
        <div className={cn(
          "flex flex-wrap items-start gap-3 rounded-2xl border px-4 py-3 text-sm",
          missing ? "border-warning/40 bg-warning/10" : "border-success/30 bg-success/5",
        )}>
          {missing
            ? <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
            : <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />}
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              {missing
                ? `${missing} ${missing === 1 ? "call" : "calls"} in Smartflo ${missing === 1 ? "isn't" : "aren't"} in the panel yet`
                : "The panel has every call Smartflo has today"}
            </p>
            <p className="text-muted-foreground tabular">
              Smartflo: {data.smartfloCount} · panel: {data.panelCount} · numbers checked: {data.dids.map((d: string) => formatDid(d)).join(", ")}
            </p>
            {missing > 0 && (
              <p className="mt-1 text-muted-foreground">
                Usually a webhook that didn't arrive. Running the CDR poller for this dealership imports them.
              </p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn("size-4", isFetching && "animate-spin")} /> Check again
          </Button>
        </div>
      ) : null}
    </DashboardView>
  );
}
